import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Loader2, Plus, Trash2, ShieldAlert, Search } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'sonner';

const REASON_LABELS = {
  hard_bounce: { label: 'Hard Bounce', color: 'bg-rose-100 text-rose-700' },
  soft_bounce_threshold: { label: 'Soft Bounce', color: 'bg-amber-100 text-amber-700' },
  complaint: { label: 'Spam Complaint', color: 'bg-purple-100 text-purple-700' },
  manual_unsubscribe: { label: 'Unsubscribed', color: 'bg-slate-100 text-slate-700' },
  admin_manual: { label: 'Admin Added', color: 'bg-blue-100 text-blue-700' },
};

export default function EmailSuppressions() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [addEmail, setAddEmail] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/email/suppressions?limit=500');
      setRows(data.suppressions || []);
      setTotal(data.total || 0);
    } catch {
      toast.error('Failed to load suppression list');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async () => {
    if (!addEmail.trim()) { toast.error('Email is required'); return; }
    try {
      await api.post('/email/suppressions', { email: addEmail.trim(), reason: 'admin_manual' });
      toast.success(`${addEmail} added to suppression list`);
      setAddEmail('');
      setShowAdd(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add');
    }
  };

  const handleRemove = async (email) => {
    if (!window.confirm(`Remove ${email} from suppression list? They'll start receiving marketing emails again.`)) return;
    try {
      await api.delete(`/email/suppressions/${encodeURIComponent(email)}`);
      toast.success('Removed');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to remove');
    }
  };

  const filtered = rows.filter((r) => !filter || r.email.toLowerCase().includes(filter.toLowerCase()));

  return (
    <DashboardLayout title="Email Suppression List">
    <div className="p-6 space-y-4" data-testid="email-suppressions-page">
      <div className="space-y-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-amber-600" />Email Suppression List</h1>
          <p className="text-sm text-slate-500">Recipients who unsubscribed, bounced, or filed spam complaints. Marketing emails skip these. Transactional emails still send.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
        <Button onClick={() => setShowAdd(true)} data-testid="add-suppression-btn"><Plus className="h-4 w-4 mr-2" />Add Email</Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Suppressed ({total})</CardTitle>
          <CardDescription>Sorted by most recent.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="relative mb-3">
            <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
            <Input className="pl-8" placeholder="Filter by email…" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="suppression-filter" />
          </div>
          {loading ? (
            <div className="flex items-center gap-2 text-slate-500 py-6"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-10 text-slate-400 text-sm">{rows.length === 0 ? 'No suppressed emails yet.' : 'No matches for that filter.'}</div>
          ) : (
            <div className="space-y-1.5">
              {filtered.map((r) => {
                const reason = REASON_LABELS[r.last_reason || r.reason] || { label: r.reason || '—', color: 'bg-slate-100 text-slate-600' };
                return (
                  <div key={r.id || r.email} className="flex items-center justify-between p-2.5 border rounded-md hover:bg-slate-50 text-sm" data-testid={`suppression-row-${r.email}`}>
                    <div className="flex-1">
                      <p className="font-medium text-slate-700">{r.email}</p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {new Date(r.last_seen_at || r.suppressed_at).toLocaleString()} · source: {r.last_source || r.source || '—'}
                      </p>
                    </div>
                    <Badge className={reason.color}>{reason.label}</Badge>
                    <Button variant="ghost" size="icon" onClick={() => handleRemove(r.email)} data-testid={`remove-suppression-${r.email}`}>
                      <Trash2 className="h-4 w-4 text-rose-600" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add to Suppression List</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Email address</Label>
            <Input value={addEmail} onChange={(e) => setAddEmail(e.target.value)} placeholder="user@example.com" data-testid="add-suppression-input" />
            <p className="text-xs text-slate-500">This email will no longer receive any marketing campaigns. Transactional emails (invoices, schedule changes) will still send.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button onClick={handleAdd} data-testid="confirm-add-suppression">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </DashboardLayout>
  );
}
